import { useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { X } from "lucide-react"
import { api } from "@/app/api/client"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { useClientTheme } from "@/app/components/ThemeContext"
import { LoadingState } from "@/app/components/LoadingState"
import { DataCard } from "@/app/components/DataCard"
import type { InventoryRow } from "@/app/components/InventoryTable"
import type { Transaction } from "@/app/types/inventory"

interface TransactionsResponse {
  data: Transaction[]
  meta: { total_count: number; page: number; per_page: number; total_pages: number }
}

interface Props {
  str: string
  township_range: string
  holding: InventoryRow
  totalOrri: number | null
  onClose: () => void
}

const TX_HEADERS = ["TX #", "Date", "Type", "Net Acres", "NRI", "ORRI %", "Counterparty"]

export function SectionModal({ str, township_range, holding, totalOrri, onClose }: Props) {
  const { theme } = useClientTheme()
  const isMobile = useIsMobile()
  const sectionNumber = str.split("-")[0]

  const { data, isLoading, error } = useQuery<TransactionsResponse>({
    queryKey: ["transactions", "str", str],
    queryFn: () =>
      api.get<TransactionsResponse>(`/transactions?str=${encodeURIComponent(str)}&per_page=200`),
  })

  const transactions = data?.data ?? []

  // Close on ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose])

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [])

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center ${isMobile ? "items-stretch" : "items-center p-4"}`}
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
    >
      <div
        className={`relative overflow-hidden flex flex-col ${isMobile ? "" : "rounded-2xl"}`}
        style={{
          backgroundColor: theme.bgSurface,
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          width: isMobile ? "100vw" : "min(900px, 95vw)",
          height: isMobile ? "100dvh" : undefined,
          maxHeight: isMobile ? "100dvh" : "88vh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-6 py-4"
          style={{ borderBottom: `1px solid ${theme.borderColor}`, flexShrink: 0 }}
        >
          <div>
            <h2
              style={{
                fontFamily: "var(--font-data)",
                fontSize: "17px",
                fontWeight: 700,
                color: theme.textPrimary,
              }}
            >
              Section {sectionNumber} — {township_range}
            </h2>
            <p
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "12px",
                color: theme.textMuted,
                marginTop: "2px",
              }}
            >
              {str}
              {holding.county ? ` · ${holding.county} County` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ml-4"
            style={{
              color: theme.textMuted,
              backgroundColor: theme.bgSurface2,
              border: `1px solid ${theme.borderColor}`,
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Summary stats */}
        <div
          className="grid grid-cols-3 px-6 py-5"
          style={{
            borderBottom: `1px solid ${theme.borderColor}`,
            backgroundColor: theme.bgSurface2,
            flexShrink: 0,
          }}
        >
          {[
            {
              label: "Total Net Acres",
              value: Number(holding.net_acres).toFixed(4),
              color: theme.brandTeal,
            },
            {
              label: "NRI",
              value: `${(Number(holding.weighted_nri) * 100).toFixed(4)}%`,
              color: theme.textPrimary,
            },
            {
              label: "ORRI",
              value: totalOrri !== null ? `${(totalOrri * 100).toFixed(4)}%` : "—",
              color: totalOrri ? theme.brandAccent : theme.textDim,
            },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col gap-1">
              <span
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: "10px",
                  fontWeight: 600,
                  color: theme.textDim,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                }}
              >
                {stat.label}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-data)",
                  fontSize: "22px",
                  fontWeight: 700,
                  color: stat.color,
                  letterSpacing: "-0.01em",
                }}
              >
                {stat.value}
              </span>
            </div>
          ))}
        </div>

        {/* Transaction history */}
        <div className="flex flex-col overflow-hidden flex-1">
          <div
            className="px-6 py-3"
            style={{ borderBottom: `1px solid ${theme.borderColor}`, flexShrink: 0 }}
          >
            <h3
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "13px",
                fontWeight: 600,
                color: theme.textPrimary,
              }}
            >
              Transaction History
            </h3>
          </div>

          {isLoading ? (
            <LoadingState theme={theme} />
          ) : error ? (
            <div
              className="px-6 py-10 text-center flex-1"
              style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.brandAccent }}
            >
              Failed to load transactions for this section. Please try again.
            </div>
          ) : isMobile ? (
            transactions.length === 0 ? (
              <div
                className="px-6 py-10 text-center"
                style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textDim }}
              >
                No transactions recorded for this section.
              </div>
            ) : (
              <div className="overflow-auto flex-1 flex flex-col gap-2.5 px-4 py-3">
                {transactions.map((tx) => {
                  const na = Number(tx.net_acres)
                  const orri = Number(tx.orri_reserved)
                  const isDiv = tx.type === "DIV" || na < 0
                  const typeColor = isDiv ? theme.brandAccent : theme.brandTeal
                  return (
                    <DataCard
                      key={tx.id}
                      title={tx.tx_number}
                      accessory={
                        <span
                          style={{
                            fontFamily: "var(--font-ui)",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: typeColor,
                          }}
                        >
                          {tx.type}
                        </span>
                      }
                      fields={[
                        { label: "Date", value: tx.transaction_date },
                        {
                          label: "Net Acres",
                          value: <span style={{ color: typeColor }}>{na.toFixed(4)}</span>,
                          mono: true,
                        },
                        { label: "NRI", value: Number(tx.nri).toFixed(4), mono: true },
                        {
                          label: "ORRI %",
                          value: orri > 0 ? `${(orri * 100).toFixed(4)}%` : "—",
                          mono: true,
                        },
                        { label: "Counterparty", full: true, value: tx.counterparty ?? "—" },
                      ]}
                    />
                  )
                })}
              </div>
            )
          ) : (
            <div className="overflow-auto flex-1">
              <table className="w-full border-collapse">
                <thead>
                  <tr
                    style={{
                      backgroundColor: theme.bgSurface2,
                      borderBottom: `1px solid ${theme.borderColor}`,
                      position: "sticky",
                      top: 0,
                    }}
                  >
                    {TX_HEADERS.map((label) => (
                      <th
                        key={label}
                        className="px-4 py-2.5 text-left"
                        style={{
                          fontFamily: "var(--font-ui)",
                          fontSize: "11px",
                          fontWeight: 600,
                          color: theme.textDim,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr>
                      <td
                        colSpan={TX_HEADERS.length}
                        className="px-4 py-10 text-center"
                        style={{
                          fontFamily: "var(--font-ui)",
                          fontSize: "13px",
                          color: theme.textDim,
                        }}
                      >
                        No transactions recorded for this section.
                      </td>
                    </tr>
                  ) : (
                    transactions.map((tx) => {
                      const na = Number(tx.net_acres)
                      const orri = Number(tx.orri_reserved)
                      const isDiv = tx.type === "DIV" || na < 0
                      const typeColor = isDiv ? theme.brandAccent : theme.brandTeal
                      return (
                        <tr key={tx.id} style={{ borderBottom: `1px solid ${theme.borderSubtle}` }}>
                          <td className="px-4 py-2.5">
                            <span
                              style={{
                                fontFamily: "var(--font-data)",
                                fontSize: "11px",
                                fontWeight: 500,
                                color: theme.textPrimary,
                              }}
                            >
                              {tx.tx_number}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span
                              style={{
                                fontFamily: "var(--font-ui)",
                                fontSize: "11px",
                                color: theme.textMuted,
                              }}
                            >
                              {tx.transaction_date}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span
                              style={{
                                fontFamily: "var(--font-ui)",
                                fontSize: "11px",
                                fontWeight: 600,
                                color: typeColor,
                              }}
                            >
                              {tx.type}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <span
                              style={{
                                fontFamily: "var(--font-data)",
                                fontSize: "11px",
                                color: typeColor,
                              }}
                            >
                              {na.toFixed(4)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <span
                              style={{
                                fontFamily: "var(--font-data)",
                                fontSize: "11px",
                                color: theme.textPrimary,
                              }}
                            >
                              {Number(tx.nri).toFixed(4)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <span
                              style={{
                                fontFamily: "var(--font-data)",
                                fontSize: "11px",
                                color: orri > 0 ? theme.textPrimary : theme.textDim,
                              }}
                            >
                              {orri > 0 ? `${(orri * 100).toFixed(4)}%` : "—"}
                            </span>
                          </td>
                          <td className="px-4 py-2.5">
                            <span
                              style={{
                                fontFamily: "var(--font-ui)",
                                fontSize: "11px",
                                color: theme.textPrimary,
                              }}
                            >
                              {tx.counterparty ?? "—"}
                            </span>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
